/*
 * Collection filtering & sorting — self-contained, no external libraries.
 *
 * Replaces the CollectionSection + <price-range> logic that used to come from
 * facets.js / libs.min.js (domDelegate, GSAP, Flickity, fastdom, Rellax, ...).
 * Only the AJAX filtering/sorting and the price-range slider lived in facets.js
 * that mattered on this template — everything else (accordions, stock toggle,
 * static pills, mobile panel open/close, mobile sort, infinite scroll) is
 * handled by the section's inline scripts, scripts.js (jQuery) and ajaxinate.js.
 *
 * Loaded only by sections/main-collection-product-grid.liquid.
 */
(function () {
  'use strict';

  /* -------------------------------------------------------------------------
   * <price-range> custom element (ported 1:1 from facets.js)
   * Keeps the two range sliders and the two number inputs in sync, and fires a
   * bubbling `change` so the form-level handler below triggers a reload.
   * ---------------------------------------------------------------------- */
  if (window.customElements && !window.customElements.get('price-range')) {
    class PriceRange extends HTMLElement {
      connectedCallback() {
        this.rangeLowerBound = this.querySelector('.price-range__range-group input:first-child');
        this.rangeHigherBound = this.querySelector('.price-range__range-group input:last-child');
        // Both bounds are split into a text field (display — shows thousands
        // separators, e.g. "5,000") and a hidden field (the raw number actually
        // submitted to Shopify; a comma would be an invalid filter value).
        this.textInputLowerBound = this.querySelector('.price-range__display-lower');
        this.valueLower = this.querySelector('.price-range__value-lower');
        this.displayHigher = this.querySelector('.price-range__display-higher');
        this.valueHigher = this.querySelector('.price-range__value-higher');

        if (!this.rangeLowerBound || !this.textInputLowerBound || !this.valueLower || !this.displayHigher || !this.valueHigher) {
          return;
        }

        this.maxCap = parseInt(this.rangeHigherBound.max);
        this.plusLabel = this.displayHigher.getAttribute('data-plus-label') || (this.maxCap + '+');

        // Add thousands separators for display; strip them back out for arithmetic.
        const fmt = (n) => { n = parseInt(n); return isNaN(n) ? '' : n.toLocaleString('en-US'); };
        const toNum = (v) => parseInt(String(v).replace(/[^\d]/g, ''), 10);

        // Current numeric upper bound (falls back to the cap when it's "10,000+")
        const higherNumeric = () => {
          return this.valueHigher.value !== '' ? parseInt(this.valueHigher.value) : this.maxCap;
        };
        const lowerNumeric = () => {
          const raw = toNum(this.textInputLowerBound.value);
          return isNaN(raw) ? parseInt(this.rangeLowerBound.min || 0) : raw;
        };

        // Apply an upper bound. At the cap we show "10k+" and clear the submitted
        // value so Shopify returns everything above the cap too.
        const commitHigher = (value, notify) => {
          value = parseInt(value);
          if (isNaN(value)) { value = this.maxCap; }
          value = Math.min(Math.max(value, 0), this.maxCap);

          this.rangeHigherBound.value = value;
          this.rangeHigherBound.parentElement.style.setProperty('--range-max', (value / this.maxCap * 100) + '%');

          if (value >= this.maxCap) {
            this.displayHigher.value = this.plusLabel;
            this.valueHigher.value = '';
          } else {
            this.displayHigher.value = fmt(value);
            this.valueHigher.value = value;
          }

          if (notify) {
            this.valueHigher.dispatchEvent(new Event('change', { bubbles: true }));
          }
        };

        // Apply a lower bound: sync the slider, the display field (with thousands
        // separators) and the hidden raw value that's submitted. A bound of 0 is
        // treated as "no minimum" so we don't submit a redundant gte=0 param
        // (which would otherwise flag the price filter as active).
        const commitLower = (value, notify) => {
          value = parseInt(value);
          if (isNaN(value)) { value = 0; }
          value = Math.min(Math.max(value, 0), higherNumeric() - 1);

          this.rangeLowerBound.value = value;
          this.rangeLowerBound.parentElement.style.setProperty('--range-min', (value / this.maxCap * 100) + '%');

          if (value > 0) {
            this.textInputLowerBound.value = fmt(value);
            this.valueLower.value = value;
          } else {
            this.textInputLowerBound.value = '';
            this.valueLower.value = '';
          }

          if (notify) {
            this.valueLower.dispatchEvent(new Event('change', { bubbles: true }));
          }
        };

        // Select the whole text on focus for a better UX
        this.textInputLowerBound.addEventListener('focus', () => this.textInputLowerBound.select());
        this.displayHigher.addEventListener('focus', () => this.displayHigher.select());

        // ---- number / text inputs ----
        this.textInputLowerBound.addEventListener('change', (event) => {
          commitLower(toNum(event.target.value), true);
        });
        this.displayHigher.addEventListener('change', () => {
          const digits = (this.displayHigher.value || '').replace(/[^\d]/g, '');
          let value = digits === '' ? this.maxCap : parseInt(digits);
          value = Math.max(value, lowerNumeric() + 1);
          commitHigher(value, true);
        });

        // ---- range sliders ----
        this.rangeLowerBound.addEventListener('change', (event) => {
          commitLower(event.target.value, true);
        });
        this.rangeHigherBound.addEventListener('change', (event) => {
          commitHigher(event.target.value, true);
        });

        // Bound the two range sliders against each other while dragging (no reload)
        this.rangeLowerBound.addEventListener('input', (event) => {
          commitLower(event.target.value, false);
        });
        this.rangeHigherBound.addEventListener('input', (event) => {
          const value = Math.max(parseInt(event.target.value), lowerNumeric() + 1);
          event.target.value = value;
          commitHigher(value, false);
        });

        // Render the initial bounds with thousands separators (e.g. "5,000").
        this.textInputLowerBound.value = fmt(this.textInputLowerBound.value);
        if (this.displayHigher.value !== this.plusLabel) {
          this.displayHigher.value = fmt(this.displayHigher.value);
        }
      }
    }

    window.customElements.define('price-range', PriceRange);
  }

  /* -------------------------------------------------------------------------
   * Collection filtering / sorting (ported from CollectionSection in facets.js)
   * ---------------------------------------------------------------------- */
  function initCollection(section) {
    if (section.dataset.ajaxFiltersReady === 'true') {
      return;
    }
    section.dataset.ajaxFiltersReady = 'true';

    var settings = {};
    try {
      settings = JSON.parse(section.getAttribute('data-section-settings') || '{}');
    } catch (e) {}

    var sectionId = settings.sectionId;
    var productsElement = section.querySelector('.CollectionInner__Products');
    var abortController = null;

    function lapAndUp() {
      return window.matchMedia('screen and (min-width: 1008px)').matches;
    }

    function reloadProducts(url) {
      if (!productsElement || !url) {
        return;
      }

      if (abortController) {
        abortController.abort();
      }

      document.dispatchEvent(new CustomEvent('theme:loading:start'));
      // Lightweight loading feedback (no animation library needed)
      productsElement.style.opacity = '0.5';
      productsElement.style.pointerEvents = 'none';

      if (window.history && window.history.pushState) {
        window.history.pushState({ path: url }, '', url);
      }

      var fetchUrl = new URL(url, window.location.origin);
      if (sectionId) {
        fetchUrl.searchParams.set('section_id', sectionId);
      }

      abortController = new AbortController();

      fetch(fetchUrl.toString(), { signal: abortController.signal })
        .then(function (response) {
          return response.text();
        })
        .then(function (content) {
          var temp = document.createElement('div');
          temp.innerHTML = content;

          // Swap the product grid
          var newProducts = temp.querySelector('.CollectionInner__Products');
          if (newProducts) {
            productsElement.innerHTML = newProducts.innerHTML;
          }

          // Update the APPLY button count with the live (filtered) product count.
          // The button lives outside the swapped regions, so update it explicitly.
          var newCount = temp.querySelector('.FilterApply__count');
          if (newCount) {
            Array.prototype.forEach.call(section.querySelectorAll('.FilterApply__count'), function (el) {
              el.textContent = newCount.textContent;
            });
          }

          // Swap the toolbar filter button (active count + classes)
          var newToolbarFilter = temp.querySelector('.CollectionToolbar__Item--filter');
          var toolbarFilter = section.querySelector('.CollectionToolbar__Item--filter');
          if (newToolbarFilter && toolbarFilter) {
            toolbarFilter.innerHTML = newToolbarFilter.innerHTML;
            toolbarFilter.className = newToolbarFilter.className;
          }

          // Swap the "Filter & Sort" header active-count + Clear All block.
          // It lives outside the swapped regions, so update it explicitly.
          var newFilterHead = temp.querySelector('.FilterHead__active');
          var filterHead = section.querySelector('.FilterHead__active');
          if (newFilterHead && filterHead) {
            filterHead.innerHTML = newFilterHead.innerHTML;
          }

          // Swap the filter forms (drawer + sidebar), keeping open sections open
          ['#collection-filters-drawer-form', '#collection-filters-sidebar-form'].forEach(function (selector) {
            var current = section.querySelector(selector);
            var updated = temp.querySelector(selector);
            if (!current || !updated) {
              return;
            }

            Array.prototype.forEach.call(current.querySelectorAll('.Collapsible'), function (collapsible) {
              var button = collapsible.querySelector('[aria-expanded]');
              if (!button || button.getAttribute('aria-expanded') !== 'true') {
                return;
              }
              var match = updated.querySelector('[data-filter-index="' + collapsible.getAttribute('data-filter-index') + '"]');
              if (!match) {
                return;
              }
              // Open state is driven purely by aria-expanded (CSS grid animation),
              // so just carry the attribute over — no inline height/overflow.
              var newButton = match.querySelector('[aria-expanded]');
              if (newButton) {
                newButton.setAttribute('aria-expanded', 'true');
              }
            });

            current.innerHTML = updated.innerHTML;
          });

          productsElement.style.opacity = '';
          productsElement.style.pointerEvents = '';
          document.dispatchEvent(new CustomEvent('theme:loading:end'));

          // Scroll back to the top of the collection if it's out of view
          var main = section.querySelector('.CollectionMain');
          if (main) {
            var headerHeight = parseInt(document.documentElement.style.getPropertyValue('--header-height')) || 0;
            var offset = main.getBoundingClientRect().top - headerHeight;
            var toolbar = section.querySelector('.CollectionToolbar');
            if (lapAndUp() && toolbar && toolbar.clientHeight === 0) {
              offset -= 50;
            }
            if (offset < 0) {
              window.scrollBy({ top: offset, behavior: 'smooth' });
            }
          }
        })
        .catch(function () {
          // Ignore aborted requests; restore the grid on genuine failure
          productsElement.style.opacity = '';
          productsElement.style.pointerEvents = '';
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        });
    }

    // Filter checkbox / price input change -> reload
    section.addEventListener('change', function (event) {
      var input = event.target.closest('[name^="filter."]');
      if (!input) {
        return;
      }
      var form = input.closest('form');
      if (!form) {
        return;
      }
      var params = new URLSearchParams(new FormData(form)).toString();
      reloadProducts(window.location.pathname + '?' + params);
    });

    // Clear / reset filters
    section.addEventListener('click', function (event) {
      var clear = event.target.closest('[data-action="clear-filters"]');
      if (!clear) {
        return;
      }
      event.preventDefault();
      reloadProducts(clear.getAttribute('data-url'));
    });

    // Sort links -> AJAX. Merges the new sort into the current URL so active
    // filters are preserved (the raw links point at the bare collection URL).
    // Covers the sidebar sort pills and the mobile "Sort by" menu.
    section.addEventListener('click', function (event) {
      var link = event.target.closest('.PillRow--sort a[href], .select-menu-mobile a[href]');
      if (!link) {
        return;
      }
      var sortBy = null;
      try {
        sortBy = new URL(link.href, window.location.origin).searchParams.get('sort_by');
      } catch (e) {}
      if (!sortBy) {
        return; // not a sort link — let it behave normally
      }
      event.preventDefault();

      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortBy);
      url.searchParams.delete('page');
      reloadProducts(url.toString());

      // Close the mobile sort menu if it happens to be open
      var openMenu = document.querySelector('.select-menu-mobile.active, .select-dropdown .dropdown-menu.active');
      if (openMenu) {
        openMenu.classList.remove('active');
      }
    });

    // Swipe-to-close for the filter/sort drawer on mobile
    initFilterSwipe(section.querySelector('.collection-sidebar'));
  }

  // Swipe-to-close for the filter/sort slide-out (mobile). The drawer opens from
  // the right, so a rightward swipe past the threshold closes it. Mirrors the
  // slide-out cart: direction is locked so vertical scrolling still works, and
  // horizontal controls (sort pills, price sliders) are left alone.
  function initFilterSwipe(sidebar) {
    if (!sidebar || sidebar.dataset.swipeReady === 'true') {
      return;
    }
    sidebar.dataset.swipeReady = 'true';

    var startX = 0, startY = 0, currentX = 0;
    var isDragging = false, isHorizontalSwipe = false, startedOnControl = false;

    function isMobile() { return window.innerWidth <= 992; }
    function isOpen() { return sidebar.classList.contains('active'); }

    sidebar.addEventListener('touchstart', function (event) {
      if (!isMobile() || !isOpen()) {
        return;
      }
      var touch = event.touches[0];
      startX = currentX = touch.clientX;
      startY = touch.clientY;
      isDragging = true;
      isHorizontalSwipe = false;
      // Don't hijack gestures that begin on horizontal controls
      startedOnControl = !!event.target.closest('.PillRow--sort, .price-range, input[type="range"]');
    }, { passive: true });

    sidebar.addEventListener('touchmove', function (event) {
      if (!isDragging || !isMobile() || startedOnControl) {
        return;
      }
      var touch = event.touches[0];
      currentX = touch.clientX;
      var deltaX = currentX - startX;
      var deltaY = touch.clientY - startY;

      // Lock the direction on the first meaningful move
      if (!isHorizontalSwipe && Math.abs(deltaX) + Math.abs(deltaY) > 10) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          isHorizontalSwipe = true;
        } else {
          isDragging = false; // vertical scroll — let the list scroll
          return;
        }
      }
      if (!isHorizontalSwipe) {
        return;
      }

      // Follow the finger only when swiping right (toward close)
      if (deltaX > 0) {
        sidebar.style.transition = 'none';
        sidebar.style.transform = 'translateX(' + deltaX + 'px)';
      }
    }, { passive: true });

    function finalizeClose() {
      // Hand off from the inline transform to the CSS closed state without
      // animating. Both are translateX(100%), so there's no visible jump.
      sidebar.style.transition = 'none';
      sidebar.classList.remove('active');
      sidebar.style.transform = '';
      void sidebar.offsetHeight; // commit the instant change before restoring transition
      sidebar.style.transition = '';
    }

    function endSwipe() {
      if (!isDragging) {
        return;
      }
      isDragging = false;
      var wasHorizontal = isHorizontalSwipe;
      isHorizontalSwipe = false;

      var deltaX = currentX - startX;
      var threshold = Math.min(120, sidebar.offsetWidth * 0.25);

      if (wasHorizontal && deltaX > threshold) {
        // Drop the dark overlay now, in sync with the drawer sliding out (mirrors
        // how the cart clears its overlay the moment it starts closing).
        document.body.classList.remove('filters-open');
        // Continue the SAME transform off-screen, then finalize once hidden.
        // Duration matches the slide-out cart (0.3s).
        sidebar.style.transition = 'transform .3s ease';
        sidebar.style.transform = 'translateX(100%)';
        var closed = false;
        var onEnd = function (e) {
          if (e && e.propertyName && e.propertyName !== 'transform') {
            return;
          }
          if (closed) {
            return;
          }
          closed = true;
          sidebar.removeEventListener('transitionend', onEnd);
          finalizeClose();
        };
        sidebar.addEventListener('transitionend', onEnd);
        setTimeout(onEnd, 360); // fallback if transitionend doesn't fire (just after the .3s transition)
      } else {
        // Didn't pass the threshold — slide back to fully open
        sidebar.style.transition = 'transform .2s ease';
        sidebar.style.transform = '';
        var onReset = function () {
          sidebar.removeEventListener('transitionend', onReset);
          sidebar.style.transition = '';
        };
        sidebar.addEventListener('transitionend', onReset);
      }
    }

    sidebar.addEventListener('touchend', endSwipe, { passive: true });
    sidebar.addEventListener('touchcancel', endSwipe, { passive: true });
  }

  // Back / forward navigation restores the URL with a full reload (matches the
  // previous behaviour). Bound once, not per-section.
  window.addEventListener('popstate', function (event) {
    if (event.state && event.state.path) {
      window.location.href = event.state.path;
    }
  });

  function init() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-section-type="collection"]'),
      initCollection
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
